import React, { useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { BarItem } from "@nivo/bar";
import ChartCard from "../ChartCard";
import { getComparisons } from "../../../utils/helpers";
import { propTypes } from "react-bootstrap/esm/Image";

const MIN_BAR_LENGTH = 2;
// Fixed height (px) for "<1%" diagnosis bars so they render visibly and stay
// hoverable even though their numeric value rounds to 0.
const MIN_BAR_HEIGHT = 3;

const CustomTooltip = ({ data, showPercentOfTotal, overallTotal }) => {
    const categoryTotal = Number(data.totalRecords) || 0;
    const percentOfTotal =
        overallTotal > 0 ? (categoryTotal / overallTotal) * 100 : 0;
    return (
        <div className="dashboard-tooltip">
            <label>
                {showPercentOfTotal
                    ? `Percent of Total: ${percentOfTotal < 1 ? "<1" : Math.round(percentOfTotal)}%`
                    : `Total count: ${categoryTotal}`}
            </label>
            <div className="tooltip-Arrow"></div>
        </div>
    );
};

CustomTooltip.propTypes = {
    data: PropTypes.any,
    showPercentOfTotal: PropTypes.bool,
    overallTotal: PropTypes.number,
};

const ReusableBarChart = ({
    chartId,
    title,
    accessibilityText,
    axisLabelOrientation,
    data,
    indexBy,
    selectedCompanyYourCompanyTo,
    organizationName,
    description,
    // Add new props for data passing
    tableData,
    reportDetails,
    layout,
    chartValueOrientation,
    chartValueType,
    disabilitySnapshot,
    totalRecords,
    errorMessage,
    viewId,
    disclaimer,
    // Cache-related props
    cacheKey,
    clientClaimCount,
}) => {
    const isRotate = axisLabelOrientation === "rotate";
    const maxDurationGraph =
        disabilitySnapshot && title === "STD Claims Approaching Max Duration"
            ? true
            : false;

    const comparisonMap = useMemo(
        () => getComparisons(organizationName),
        [organizationName]
    );
    let selectedComparisonText;
    if (selectedCompanyYourCompanyTo?.length === 2) {
        selectedComparisonText = "All";
    } else if (selectedCompanyYourCompanyTo?.length === 1) {
        const value = selectedCompanyYourCompanyTo[0].value;
        if (value === "industryPeers") {
            selectedComparisonText = "Industry Peers";
        } else if (value === "unumBlock") {
            selectedComparisonText = "Unum Clients";
        } else {
            selectedComparisonText = value;
        }
    } else {
        selectedComparisonText = "Organization Only";
    }
    const comparisons = useMemo(
        () => comparisonMap[selectedComparisonText] || [],
        [comparisonMap, selectedComparisonText]
    );

    // For horizontal charts (e.g. "Open claims by diagnosis category") the bars
    // are stacked vertically, so a large number of bars looks clustered at the
    // fixed default height. When there are more than 20 bars we grow the chart
    // height, allocating extra vertical space per additional bar so the data
    // renders cleanly and remains readable.
    const dynamicChartHeight = useMemo(() => {
        const barCount = data?.length ?? 0;
        if (layout === "horizontal" && barCount > 20) {
            const baseHeight = 400;
            const perBar = 22;
            return baseHeight + (barCount - 20) * perBar;
        }
        return undefined;
    }, [data, layout]);

    // Custom layer for Y-axis label at top right
    const ClaimsLabelLayer = useCallback(
        ({ innerWidth }) => (
            <text
                x={innerWidth + 40}
                y={-40}
                textAnchor="end"
                className="charts-claims-label"
            >
                Claims count
            </text>
        ),
        []
    );

    // Wraps the default Nivo bar so tiny non-zero bars (e.g. value 1) stay
    // visible when the axis max is large, while keeping the built-in colors,
    // hover/tooltip and labels intact.
    const MinWidthBarItem = useCallback((barItemProps) => {
        const { bar, style } = barItemProps;
        const value = bar?.data?.value ?? 0;
        if (value > 0 && bar.width < MIN_BAR_LENGTH) {
            return (
                <BarItem
                    {...barItemProps}
                    bar={{ ...bar, width: MIN_BAR_LENGTH }}
                    style={{ ...style, width: MIN_BAR_LENGTH }}
                />
            );
        }
        return <BarItem {...barItemProps} />;
    }, []);

    // Diagnosis chart only: give "<1%" bars a fixed visible height that grows
    // upward from the baseline, so they render and keep the shared hover tooltip.
    const MinHeightBarItem = useCallback((barItemProps) => {
        const { bar, style } = barItemProps;
        const isBelowOne = bar?.data?.data?.orgValueDisplay === "<1";
        if (isBelowOne && bar.height < MIN_BAR_HEIGHT) {
            const newY = bar.y + bar.height - MIN_BAR_HEIGHT;
            return (
                <BarItem
                    {...barItemProps}
                    bar={{ ...bar, y: newY, height: MIN_BAR_HEIGHT }}
                    style={{
                        ...style,
                        height: MIN_BAR_HEIGHT,
                        transform: `translate(${bar.x}, ${newY})`,
                    }}
                />
            );
        }
        return <BarItem {...barItemProps} />;
    }, []);

    const BarLabelLayer = useCallback(
        ({ bars }) => (
            <>
                {bars.map((bar) => {
                    let x, y, textAnchor, alignmentBaseline;
                    if (layout === "vertical") {
                        x = bar.x + bar.width / 2;
                        // "<1%" bars are lifted by MinHeightBarItem, so measure
                        // the label offset from the actual rendered bar top.
                        const isBelowOne =
                            bar.data?.data?.orgValueDisplay === "<1";
                        const barTop =
                            isBelowOne && bar.height < MIN_BAR_HEIGHT
                                ? bar.y + bar.height - MIN_BAR_HEIGHT
                                : bar.y;
                        y =
                            barTop -
                            (chartValueOrientation === "vertical" ? 12 : 6);
                        textAnchor = "middle";
                        alignmentBaseline = "middle";
                    } else {
                        x =
                            bar.x +
                            bar.width +
                            (chartValueOrientation === "vertical" ? 30 : 8);
                        y = bar.y + bar.height / 2;
                        textAnchor = "start";
                        alignmentBaseline = "middle";
                    }

                    const transform =
                        chartValueOrientation === "vertical"
                            ? `rotate(-90, ${x}, ${y})`
                            : undefined;

                    const rawDatum = bar.data?.data || {};
                    const seriesKey = bar.data?.id;
                    const displayField = `${seriesKey}Display`;
                    const displaySource =
                        rawDatum[displayField] ?? bar.data.value;

                    // Determine display value based on chart type
                    let displayValue;
                    if (maxDurationGraph) {
                        displayValue = displaySource;
                    } else if (chartValueType === "count") {
                        displayValue = displaySource;
                    } else {
                        displayValue = `${displaySource}%`;
                    }

                    return (
                        <text
                            key={bar.key}
                            x={x}
                            y={y}
                            textAnchor={textAnchor}
                            alignmentBaseline={alignmentBaseline}
                            transform={transform}
                            className="charts-inner-text"
                        >
                            {displayValue}
                        </text>
                    );
                })}
            </>
        ),
        [layout, chartValueOrientation, chartValueType, maxDurationGraph]
    );

    const renderTick = useCallback(
        (tick) => {
            const maxCharsPerLine = 22;

            const splitSmart = (text) => {
                if (!text || typeof text !== "string") return [""];

                text = text.trim();
                if (text.length <= maxCharsPerLine) return [text];

                const words = text.split(" ");
                let line = "";
                const lines = [];

                for (const word of words) {
                    if (
                        (line + " " + word).trim().length > maxCharsPerLine &&
                        line.length > 0
                    ) {
                        lines.push(line.trim());
                        line = word;
                    } else {
                        line += " " + word;
                    }
                }

                if (line.trim()) lines.push(line.trim());

                return lines;
            };

            const lines = splitSmart(tick?.value);

            const rotation =
                axisLabelOrientation === "diagonal"
                    ? -45
                    : axisLabelOrientation === "vertical"
                      ? -90
                      : 0;

            const translateY = tick?.y + 20;

            return (
                <g
                    transform={`translate(${tick?.x},${translateY}) rotate(${rotation})`}
                >
                    <text
                        textAnchor={rotation === 0 ? "middle" : "end"}
                        dominantBaseline="middle"
                        className="charts-inner-text"
                    >
                        {lines.map((line, i) => (
                            <tspan x="0" dy={i === 0 ? 0 : "1.1em"} key={i}>
                                {line}
                            </tspan>
                        ))}
                    </text>
                </g>
            );
        },
        [axisLabelOrientation]
    );

    const renderLeftTick = (tick) => {
        const label = tick?.value ?? "";

        return (
            <g transform={`translate(${tick?.x}, ${tick?.y})`}>
                <line
                    x1={0}
                    x2={-5}
                    y1={0}
                    y2={0}
                    stroke="#777777"
                    strokeWidth={1}
                />
                <text
                    x={-10}
                    textAnchor="end"
                    dominantBaseline="central"
                    className="charts-inner-text"
                    // style={{ fontSize: reducedLabelFontSize }}
                >
                    {label}
                </text>
            </g>
        );
    };

    const legendOffsetY = useMemo(
        () =>
            axisLabelOrientation === "diagonal"
                ? 75
                : axisLabelOrientation === "vertical"
                  ? 5
                  : 0,
        [axisLabelOrientation]
    );

    const bottomMargin = useMemo(() => 50 + legendOffsetY, [legendOffsetY]);

    const chartData = useMemo(
        () => (layout === "horizontal" ? [...data].reverse() : data),
        [layout, data]
    );

    const horizontalTickValues = useMemo(() => {
        if (
            layout !== "horizontal" ||
            chartId !== "OpenClaimsByCoverageDetails"
        ) {
            return undefined;
        }

        const maxValue = Math.max(
            0,
            ...data.map((item) => Number(item.orgValue) || 0)
        );

        return maxValue < 10
            ? Array.from({ length: maxValue + 1 }, (_, value) => value)
            : undefined;
    }, [chartId, data, layout]);

    // so labels render on a single line without being cropped.
    const leftMargin = useMemo(() => {
        if (layout !== "horizontal") return 40;
        const longest = (data || []).reduce((max, d) => {
            const len = String(d?.[indexBy] ?? "").length;
            return len > max ? len : max;
        }, 0);
        // ~6.5px per character + padding, clamped to a sensible range.
        return Math.min(Math.max(150, longest * 6.5 + 30), 300);
    }, [layout, data, indexBy]);

    // Calculate Y-axis tick values for maxDurationGraph (whole numbers only)
    const maxDurationYAxisConfig = useMemo(() => {
        if (!maxDurationGraph || !data || data.length === 0) return {};

        // Find the maximum value in the data
        const maxValue = Math.max(
            ...data.map((d) => parseInt(d.orgValue, 10) || 0)
        );

        // Calculate nice tick values based on data range
        let tickMax;
        let tickCount = 5;

        if (maxValue <= 5) {
            tickMax = 5;
        } else if (maxValue <= 10) {
            tickMax = 10;
        } else if (maxValue <= 20) {
            tickMax = 20;
        } else if (maxValue <= 50) {
            tickMax = 50;
        } else if (maxValue <= 100) {
            tickMax = 100;
        } else if (maxValue <= 200) {
            tickMax = 200;
        } else if (maxValue <= 500) {
            tickMax = 500;
        } else if (maxValue <= 1000) {
            tickMax = 1000;
        } else if (maxValue <= 2000) {
            tickMax = Math.ceil(maxValue / 500) * 500;
        } else if (maxValue <= 5000) {
            tickMax = Math.ceil(maxValue / 1000) * 1000;
        } else {
            tickMax = Math.ceil(maxValue / 2500) * 2500;
        }

        // Generate tick values
        const step = tickMax / tickCount;
        const tickValues = [];
        for (let i = 0; i <= tickCount; i++) {
            tickValues.push(step * i);
        }

        return {
            gridYValues: tickValues,
            maxValue: tickMax,
            tickValues: tickValues,
        };
    }, [maxDurationGraph, data]);

    const getRightMargin = useCallback(() => {
        if (maxDurationGraph || layout === "horizontal") return 60;
        return 30;
    }, [maxDurationGraph, layout]);

    const isCoverageHorizontalChart =
        layout === "horizontal" && chartId === "OpenClaimsByCoverageDetails";

    // Build layers array based on chart type
    const chartLayers = useMemo(() => {
        const baseLayers = [
            "grid",
            "axes",
            "bars",
            BarLabelLayer,
            "markers",
            "legends",
        ];

        if (maxDurationGraph) {
            return [
                "grid",
                "axes",
                ClaimsLabelLayer,
                "bars",
                BarLabelLayer,
                "markers",
                "legends",
            ];
        }

        return baseLayers;
    }, [BarLabelLayer, ClaimsLabelLayer, maxDurationGraph]);

    const barOptions = useMemo(
        () => ({
            data: chartData,
            keys: comparisons.map((c) => c.key),
            indexBy,
            indexScale: {
                type: "band",
                reverse: layout === "horizontal",
            },
            enableGridY: false,
            ...(maxDurationGraph
                ? {
                      enableGridY: true,
                      gridYValues: maxDurationYAxisConfig.tickValues,
                      axisRight: {
                          tickSize: 0,
                          tickPadding: 5,
                          tickValues: maxDurationYAxisConfig.tickValues,
                      },
                      minValue: 0,
                      maxValue: maxDurationYAxisConfig.maxValue,
                      borderColor: "#025780",
                  }
                : {}),
            layout,
            margin: {
                top: maxDurationGraph ? 60 : isRotate ? 90 : 50,
                right: getRightMargin(),
                bottom: bottomMargin,
                left: layout === "horizontal" ? leftMargin : 40,
            },
            axisBottom:
                layout === "horizontal"
                    ? {
                          tickSize: 5,
                          tickPadding: 5,
                          legend: "Count",
                          legendPosition: "middle",
                          legendOffset: 40,
                          ...(horizontalTickValues && {
                              tickValues: horizontalTickValues,
                          }),
                      }
                    : {
                          tickSize: 0,
                          tickPadding: 50,
                          legendPosition: "middle",
                          legendOffset: 40,
                          renderTick,
                      },
            axisLeft:
                layout === "horizontal"
                    ? {
                          tickSize: 5,
                          tickPadding: 8,
                          renderTick: renderLeftTick,
                      }
                    : null,
            ...(chartId === "OpenClaimsByCoverageDetails"
                ? { barComponent: MinWidthBarItem }
                : chartId === "OpenClaimsByDiagnosis"
                  ? { barComponent: MinHeightBarItem }
                  : {}),
            padding: 0.25,
            innerPadding: isRotate ? 3 : 8,
            groupMode: "grouped",
            borderRadius: 2,
            colors: maxDurationGraph
                ? "#4DB8C1"
                : comparisons.map((c) => c.color),
            borderColor: maxDurationGraph
                ? "#025780"
                : ({ id }) =>
                      comparisons.find((c) => c.key === id)?.borderColor ||
                      "#0199A6",
            borderWidth: 1,
            enableLabel: false,
            isInteractive: maxDurationGraph ? false : disabilitySnapshot,
            /* sonar-ignore-start */
            tooltip: (tooltipProps) => (
                <CustomTooltip
                    {...tooltipProps}
                    showPercentOfTotal={
                        chartId === "OpenClaimsByCoverageDetails"
                    }
                    overallTotal={totalRecords}
                />
            ),
            /* sonar-ignore-end */
            layers: chartLayers,
            theme: {
                axis: isCoverageHorizontalChart
                    ? {
                          domain: {
                              line: {
                                  stroke: "#777777",
                                  strokeWidth: 1,
                              },
                          },
                          ticks: {
                              line: {
                                  stroke: "#777777",
                                  strokeWidth: 1,
                              },
                              text: {
                                  fontSize: 12,
                                  fill: "#525252",
                              },
                          },
                          legend: {
                              text: {
                                  fontSize: 12,
                                  fill: "#525252",
                              },
                          },
                      }
                    : {},
                legends: {
                    text: {
                        fontSize: 12,
                        fontWeight: 400,
                        fill: "#525252",
                    },
                },
            },
            legends: undefined,
            animate: true,
            motionConfig: "gentle",
            motionStiffness: 90,
            motionDamping: 15,
        }),
        [
            chartData,
            comparisons,
            indexBy,
            layout,
            isRotate,
            legendOffsetY,
            bottomMargin,
            chartLayers,
            renderTick,
            getRightMargin,
            maxDurationGraph,
            maxDurationYAxisConfig,
            disabilitySnapshot,
            leftMargin,
            horizontalTickValues,
            chartId,
            MinWidthBarItem,
            MinHeightBarItem,
            totalRecords,
            isCoverageHorizontalChart,
        ]
    );

    // Now handle early return for error
    if (data?.length === 0 || errorMessage) {
        return (
            <ChartCard
                title={title}
                description={description}
                accessibilityText={accessibilityText}
                selectedCompanyYourCompanyTo={selectedCompanyYourCompanyTo}
                variant="bar"
                chartOptions={{}}
                isRotate={isRotate}
                errorMessage={errorMessage || "No data available"}
                disabilitySnapshot={disabilitySnapshot}
                totalRecords={totalRecords}
                viewId={viewId}
                tableData={tableData}
                reportDetails={reportDetails}
                comparisons={comparisons}
                legendOffsetY={legendOffsetY}
                disclaimer={disclaimer}
                clientClaimCount={clientClaimCount}
                chartHeight={dynamicChartHeight}
            />
        );
    }

    return (
        <ChartCard
            title={title}
            description={description}
            accessibilityText={accessibilityText}
            selectedCompanyYourCompanyTo={selectedCompanyYourCompanyTo}
            variant="bar"
            chartOptions={barOptions}
            isRotate={isRotate}
            disabilitySnapshot={disabilitySnapshot}
            totalRecords={totalRecords}
            viewId={viewId}
            tableData={tableData}
            reportDetails={reportDetails}
            comparisons={comparisons}
            legendOffsetY={legendOffsetY}
            disclaimer={disclaimer}
            cacheKey={cacheKey}
            clientClaimCount={clientClaimCount}
            chartHeight={dynamicChartHeight}
        />
    );
};

ReusableBarChart.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    accessibilityText: PropTypes.string,
    organizationName: PropTypes.string,
    axisLabelOrientation: PropTypes.string,
    selectedCompanyYourCompanyTo: PropTypes.array,
    data: PropTypes.arrayOf(
        PropTypes.objectOf(
            PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        )
    ).isRequired,
    indexBy: PropTypes.string,
    chartId: PropTypes.string,
    layout: PropTypes.string,
    chartValueOrientation: PropTypes.string,
    chartValueType: PropTypes.string,
    disabilitySnapshot: PropTypes.bool,
    totalRecords: PropTypes.number,
    errorMessage: PropTypes.string,
    viewId: PropTypes.string,
    disclaimer: PropTypes.string,
    clientClaimCount: PropTypes.number,
};

export default ReusableBarChart;
